import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import { AutoComplete, Form, Input, InputNumber, Row, Col, Select } from "antd";
import { MapContainer, TileLayer, Marker, Circle } from "react-leaflet";
import L from "leaflet";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import Icons from "components/icons";
import { Localize } from "components/service";
import { useDebounce } from "components/use-hooks";
import { GET_ME } from "graphql/query/user-gql";
import {
    DEFAULT_CHECK_RADIUS,
    DEFAULT_CHECK_COUNTRY_CODE,
    MAX_CHECK_RADIUS,
    MIN_CHECK_RADIUS,
    hasMeaningfulCheckCoordinates,
    isValidCheckRadius,
    normalizeCheckAddress,
    normalizeCheckCountryCode,
    roundCheckCoordinate,
    toCheckLocationNumber,
} from "../helpers/event-check-location-helper";
import {
    MapClickHandler,
    MapSearchController,
    MapViewController,
} from "./exhibition-location-map-controllers";
import {
    CHECK_ZOOM,
    DEFAULT_ZOOM,
    LEIPZIG_CENTER,
    PHOTON_LIMIT,
    PHOTON_MIN_QUERY_LENGTH,
    PHOTON_RATE_LIMIT_STATUS,
    PHOTON_SEARCH_URL,
} from "./exhibition-location-map-const";
import {
    getCountryOptions,
    getPhotonCoordinates,
    getPhotonExtentBounds,
    getPhotonLanguage,
    mapPhotonFeatureToOption,
    normalizeSearchQuery,
    sortPhotonOptions,
} from "./exhibition-location-map-helper";
import "leaflet/dist/leaflet.css";

const { Search } = Input;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

const ExhibitionLocationMap = ({
    event,
    form,
    markerDraggable = false,
    mapHint = false,
    addressLabel = false,
    addressPlaceholder = false,
    bindLocationMetadata = false,
    initializeDefaultRadius = true,
    searchUpdatesLocation = false,
}) => {
    const initialCountryCode = bindLocationMetadata ?
        normalizeCheckCountryCode(event?.check_country) :
        DEFAULT_CHECK_COUNTRY_CODE;
    const initialSearchText = bindLocationMetadata ? normalizeCheckAddress(event?.check_address) : "";
    const { data: meData } = useQuery(GET_ME);
    const [ searchText, setSearchText ] = useState(initialSearchText);
    const [ searchOptions, setSearchOptions ] = useState([]);
    const [ searchLoading, setSearchLoading ] = useState(false);
    const [ searchTarget, setSearchTarget ] = useState();
    const [ searchUnavailableReasonKey, setSearchUnavailableReasonKey ] = useState();
    const [ countryCode, setCountryCode ] = useState(initialCountryCode);
    const debouncedSearchText = useDebounce(searchText);
    const searchCacheRef = useRef({});
    const searchAbortControllerRef = useRef();
    const selectedSearchTextRef = useRef(initialSearchText);
    const latitude = Form.useWatch("check_latitude", form);
    const longitude = Form.useWatch("check_longitude", form);
    const radius = Form.useWatch("check_radius", form);

    const eventHasCoordinates = hasMeaningfulCheckCoordinates(event?.check_latitude, event?.check_longitude);
    const hasCoordinates = hasMeaningfulCheckCoordinates(latitude, longitude);
    const radiusValue = toCheckLocationNumber(radius);
    const hasRadius = toCheckLocationNumber(radius) !== null;
    const hasInvalidRadius = hasRadius && !isValidCheckRadius(radius);
    const searchDisabled = !!searchUnavailableReasonKey;
    const photonLanguage = getPhotonLanguage(meData?.me?.language_slug);
    const countryOptions = useMemo(() => getCountryOptions(photonLanguage), [ photonLanguage ]);

    const center = useMemo(() => (
        hasCoordinates ?
            [ toCheckLocationNumber(latitude), toCheckLocationNumber(longitude) ] :
            LEIPZIG_CENTER
    ), [ hasCoordinates, latitude, longitude ]);

    const setLocation = useCallback((nextLatitude, nextLongitude) => {
        form.setFieldsValue({
            check_latitude: roundCheckCoordinate(nextLatitude),
            check_longitude: roundCheckCoordinate(nextLongitude),
            check_radius: radiusValue || DEFAULT_CHECK_RADIUS,
        });
    }, [ form, radiusValue ]);

    const handleMapClick = useCallback(({ latlng }) => {
        setLocation(latlng.lat, latlng.lng);
    }, [ setLocation ]);

    const handleMarkerDragEnd = useCallback(({ target }) => {
        const latlng = target.getLatLng();

        setLocation(latlng.lat, latlng.lng);
    }, [ setLocation ]);

    useEffect(() => {
        const normalizedQuery = normalizeSearchQuery(debouncedSearchText);
        const cacheKey = `${ countryCode }:${ photonLanguage }:${ normalizedQuery }`;

        if (searchAbortControllerRef.current)
        {
            searchAbortControllerRef.current.abort();
        }

        if (
            searchDisabled ||
            normalizedQuery.length < PHOTON_MIN_QUERY_LENGTH ||
            normalizedQuery === normalizeSearchQuery(selectedSearchTextRef.current)
        )
        {
            setSearchLoading(false);
            setSearchOptions([]);
            return;
        }

        if (searchCacheRef.current[ cacheKey ])
        {
            setSearchOptions(searchCacheRef.current[ cacheKey ]);
            setSearchLoading(false);
            return;
        }

        const abortController = new AbortController();
        const params = new URLSearchParams({
            q: normalizedQuery,
            countrycode: countryCode,
            limit: String(PHOTON_LIMIT),
            lang: photonLanguage,
        });

        searchAbortControllerRef.current = abortController;
        setSearchLoading(true);

        fetch(`${ PHOTON_SEARCH_URL }?${ params.toString() }`, { signal: abortController.signal })
            .then((response) => {
                if (!response.ok)
                {
                    const error = new Error("Photon search failed");
                    error.status = response.status;
                    throw error;
                }

                return response.json();
            })
            .then((data) => {
                if (abortController.signal.aborted)
                {
                    return;
                }

                const options = (data?.features || [])
                    .map(mapPhotonFeatureToOption)
                    .filter(Boolean);
                const sortedOptions = sortPhotonOptions(options, normalizedQuery);

                searchCacheRef.current[ cacheKey ] = sortedOptions;
                setSearchOptions(sortedOptions);
            })
            .catch((error) => {
                if (error.name === "AbortError")
                {
                    return;
                }

                setSearchOptions([]);
                setSearchUnavailableReasonKey(
                    error.status === PHOTON_RATE_LIMIT_STATUS ?
                        "SEARCH.Error_CheckLocationRateLimit" :
                        "SEARCH.Error_CheckLocationUnavailable"
                );
            })
            .finally(() => {
                if (!abortController.signal.aborted)
                {
                    setSearchLoading(false);
                }
            });

        return () => abortController.abort();
    }, [ countryCode, debouncedSearchText, photonLanguage, searchDisabled ]);

    const handleCountryChange = useCallback((value) => {
        const nextCountryCode = normalizeCheckCountryCode(value);

        searchAbortControllerRef.current?.abort();
        selectedSearchTextRef.current = "";
        setCountryCode(nextCountryCode);
        setSearchOptions([]);
        setSearchUnavailableReasonKey(undefined);

        if (bindLocationMetadata)
        {
            form.setFieldsValue({ check_country: nextCountryCode });
        }
    }, [ bindLocationMetadata, form ]);

    const handleSearchTextChange = useCallback((value) => {
        if (normalizeSearchQuery(value) !== normalizeSearchQuery(selectedSearchTextRef.current))
        {
            selectedSearchTextRef.current = "";
        }

        setSearchText(value);

        if (bindLocationMetadata)
        {
            form.setFieldsValue({ check_address: value });
        }
    }, [ bindLocationMetadata, form ]);

    const handleSearchSelect = useCallback((value, option) => {
        const feature = option?.feature;
        const center = getPhotonCoordinates(feature);

        if (!center)
        {
            return;
        }

        selectedSearchTextRef.current = value;
        setSearchText(value);
        setSearchOptions([]);

        if (bindLocationMetadata)
        {
            form.setFieldsValue({ check_address: value });
        }

        if (searchUpdatesLocation)
        {
            setLocation(center[ 0 ], center[ 1 ]);
        }

        setSearchTarget({
            center,
            bounds: getPhotonExtentBounds(feature),
            id: `${ value }-${ Date.now() }`,
            zoom: CHECK_ZOOM,
        });
    }, [ bindLocationMetadata, form, searchUpdatesLocation, setLocation ]);

    const handleSearchSubmit = useCallback(() => {
        if (searchOptions[ 0 ])
        {
            handleSearchSelect(searchOptions[ 0 ].value, searchOptions[ 0 ]);
        }
    }, [ handleSearchSelect, searchOptions ]);

    const searchNotFoundContent = normalizeSearchQuery(searchText).length >= PHOTON_MIN_QUERY_LENGTH && !searchLoading ?
        <span className="event-location-map__search-empty"><Localize>SEARCH.Empty_CheckLocation</Localize></span> :
        null;

    return (
        <div className="event-location-map">
            { bindLocationMetadata &&
                <>
                    <Form.Item
                        hidden
                        name="check_country"
                        initialValue={ initialCountryCode }>
                        <Input />
                    </Form.Item>
                    <Form.Item
                        hidden
                        name="check_address"
                        initialValue={ initialSearchText }>
                        <Input />
                    </Form.Item>
                </>
            }

            <div className="event-location-map__search">
                <Row gutter={ 10 }>
                    <Col xs={ 24 } md={ 8 }>
                        <Form.Item
                            htmlFor="check-location-country"
                            label={ <Localize>FORMS.Input_Label_CheckLocationCountry</Localize> }>
                            <Select
                                id="check-location-country"
                                showSearch
                                suffixIcon={ <Icons.Arrow /> }
                                className="event-location-map__country-select"
                                value={ countryCode }
                                options={ countryOptions }
                                optionFilterProp="label"
                                onChange={ handleCountryChange } />
                        </Form.Item>
                    </Col>

                    <Col xs={ 24 } md={ 16 }>
                        <Form.Item
                            htmlFor="check-location-address"
                            label={ addressLabel || <Localize>FORMS.Input_Label_CheckLocationSearch</Localize> }>
                            <AutoComplete
                                className="event-location-map__search-autocomplete"
                                value={ searchText }
                                options={ searchOptions }
                                disabled={ searchDisabled }
                                notFoundContent={ searchNotFoundContent }
                                onChange={ handleSearchTextChange }
                                onSelect={ handleSearchSelect }>
                                <Search
                                    id="check-location-address"
                                    allowClear
                                    disabled={ searchDisabled }
                                    loading={ searchLoading }
                                    placeholder={ addressPlaceholder || Localize({ children: "SEARCH.Input_Placeholder_CheckLocation" }).props.children }
                                    onSearch={ handleSearchSubmit } />
                            </AutoComplete>
                        </Form.Item>
                    </Col>
                </Row>

                { searchUnavailableReasonKey &&
                    <div className="event-location-map__search-status event-location-map__search-status--error">
                        <Localize>{ searchUnavailableReasonKey }</Localize>
                    </div>
                }
            </div>

            <Row gutter={ 10 } className="event-location-map__inputs">
                <Col xs={ 24 } md={ 8 }>
                    <Form.Item
                        name="check_latitude"
                        label={ <Localize>FORMS.Input_Label_CheckLatitude</Localize> }
                        initialValue={ eventHasCoordinates ? event?.check_latitude : undefined }>
                        <InputNumber
                            min={ -90 }
                            max={ 90 }
                            step={ 0.000001 }
                            precision={ 6 }
                            controls={ false }
                            style={ { width: "100%" } } />
                    </Form.Item>
                </Col>

                <Col xs={ 24 } md={ 8 }>
                    <Form.Item
                        name="check_longitude"
                        label={ <Localize>FORMS.Input_Label_CheckLongitude</Localize> }
                        initialValue={ eventHasCoordinates ? event?.check_longitude : undefined }>
                        <InputNumber
                            min={ -180 }
                            max={ 180 }
                            step={ 0.000001 }
                            precision={ 6 }
                            controls={ false }
                            style={ { width: "100%" } } />
                    </Form.Item>
                </Col>

                <Col xs={ 24 } md={ 8 }>
                    <Form.Item
                        name="check_radius"
                        label={ <Localize>FORMS.Input_Label_CheckRadius</Localize> }
                        validateStatus={ hasInvalidRadius ? "error" : undefined }
                        help={
                            hasInvalidRadius ?
                                <Localize>FORMS.Validation_CheckRadiusRange</Localize> :
                                undefined
                        }
                        rules={ [ {
                            required: true,
                            type: "integer",
                            min: MIN_CHECK_RADIUS,
                            max: MAX_CHECK_RADIUS,
                            message: <Localize>FORMS.Validation_CheckRadiusRange</Localize>,
                        } ] }
                        initialValue={
                            !initializeDefaultRadius && !eventHasCoordinates ?
                                undefined :
                                event?.check_radius ?? (initializeDefaultRadius ? DEFAULT_CHECK_RADIUS : undefined)
                        }>
                        <InputNumber
                            step={ 1 }
                            aria-valuemin={ MIN_CHECK_RADIUS }
                            aria-valuemax={ MAX_CHECK_RADIUS }
                            controls={ false }
                            style={ { width: "100%" } } />
                    </Form.Item>
                </Col>
            </Row>

            { mapHint &&
                <div className="event-location-map__hint">{ mapHint }</div>
            }

            <div className="event-location-map__map">
                <MapContainer
                    center={ center }
                    zoom={ hasCoordinates ? CHECK_ZOOM : DEFAULT_ZOOM }
                    scrollWheelZoom={ true }
                    className="event-location-map__leaflet">
                    <TileLayer
                        attribution="&copy; OpenStreetMap contributors"
                        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <MapViewController
                        center={ center }
                        zoom={ hasCoordinates ? CHECK_ZOOM : DEFAULT_ZOOM } />
                    <MapSearchController target={ searchTarget } />
                    <MapClickHandler onMapClick={ handleMapClick } />

                    { hasCoordinates &&
                        <>
                            <Marker
                                position={ center }
                                draggable={ markerDraggable }
                                eventHandlers={ markerDraggable ? { dragend: handleMarkerDragEnd } : undefined } />
                            { radiusValue > 0 &&
                                <Circle
                                    center={ center }
                                    radius={ radiusValue }
                                    pathOptions={ {
                                        color: "#ec008c",
                                        fillColor: "#ec008c",
                                        fillOpacity: 0.18,
                                    } } />
                            }
                        </>
                    }
                </MapContainer>
            </div>
        </div>
    );
};

export default ExhibitionLocationMap;
